import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, GripVertical, Trash2, FileDown, Tv, Sparkles,
  Copy, Save, Check, Archive, ChevronDown, MoreVertical, CalendarPlus, MoveRight,
  ExternalLink, Undo2, History,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { BRAND_MONOGRAM } from "@/lib/brand";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Teleprompter from "@/components/Teleprompter";
import EspelhoTimer from "@/components/EspelhoTimer";
import RetrancaHistoryDialog from "@/components/RetrancaHistoryDialog";
import { exportEspelhoPDF, exportTeleprompterPDF } from "@/lib/pdf";
import { formatDuration, readingSeconds, totalSeconds, retrancaSeconds } from "@/lib/timing";
import { DEFAULT_OVERLAYS_SLOT } from "@/lib/constants";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import DatePickerDialog from "@/components/DatePickerDialog";

const TYPE_OPTIONS = ["VT", "Ilustra", "Entrevista", "Nota", "Bloco", "Vinheta"];

const typeColor = (t) => ({
  VT: "bg-[#152D4B] text-white",
  Ilustra: "bg-amber-500 text-white",
  Entrevista: "bg-emerald-600 text-white",
  Nota: "bg-zinc-700 text-white",
  Bloco: "bg-purple-700 text-white",
  Vinheta: "bg-pink-600 text-white",
}[t] || "bg-zinc-500 text-white");

function SortableItem({ r, active, onClick, onDelete, onCopyTo, onMoveTo }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: r.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`retranca-${r.id}`}
      className={`group flex items-stretch border ${active ? "border-[#152D4B] ring-1 ring-[#152D4B]" : "border-zinc-200 hover:border-zinc-400"} bg-white rounded-sm`}
    >
      <button
        {...attributes}
        {...listeners}
        className="px-2 cursor-grab active:cursor-grabbing border-r border-zinc-100 text-zinc-400 hover:text-zinc-700 flex items-center"
        data-testid={`drag-${r.id}`}
        aria-label="Arrastar"
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <button
        onClick={onClick}
        className="flex-1 p-3 text-left"
        data-testid={`select-retranca-${r.id}`}
      >
        <div className="flex items-center gap-2 mb-1">
          <span className={`${typeColor(r.type)} text-[10px] uppercase tracking-wider px-1.5 py-0.5 font-bold`}>
            {r.type}
          </span>
          {r.approved && (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
              <Check className="w-3 h-3" /> ok
            </span>
          )}
        </div>
        <div className="font-semibold text-sm text-zinc-900 truncate">{r.title || "Sem título"}</div>
        <div className="text-xs text-zinc-500 mt-0.5 flex gap-2 items-center flex-wrap">
          {r.presenter && <span>🎙 {r.presenter}</span>}
          {r.responsible && <span>· {r.responsible}</span>}
          <span className="text-[10px] font-mono text-zinc-400 ml-auto">
            {formatDuration(retrancaSeconds(r))}
          </span>
        </div>
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="px-2 text-zinc-300 hover:text-zinc-700 flex items-center"
            data-testid={`retranca-menu-${r.id}`}
            aria-label="Mais ações"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreVertical className="w-4 h-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="rounded-sm w-56">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em]">
            Reexibição
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid={`copy-to-${r.id}`}
            onClick={onCopyTo}
            className="cursor-pointer"
          >
            <CalendarPlus className="w-4 h-4 mr-2 text-emerald-600" />
            <div className="flex flex-col">
              <span className="text-sm">Reexibir em outra data</span>
              <span className="text-[10px] text-zinc-500">Cria cópia no novo espelho</span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid={`move-to-${r.id}`}
            onClick={onMoveTo}
            className="cursor-pointer"
          >
            <MoveRight className="w-4 h-4 mr-2 text-[#152D4B]" />
            <div className="flex flex-col">
              <span className="text-sm">Mover para outra data</span>
              <span className="text-[10px] text-zinc-500">Transfere para o novo espelho</span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid={`delete-menu-${r.id}`}
            onClick={onDelete}
            className="cursor-pointer text-red-600 focus:text-red-700"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            <span className="text-sm">Excluir retranca</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default function EspelhoPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [ep, setEp] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [tpOpen, setTpOpen] = useState(false);
  const [gaveta, setGaveta] = useState([]);
  const [dateAction, setDateAction] = useState(null); // { mode: 'copy'|'move', retrancaId }
  const [historyVersion, setHistoryVersion] = useState(0);
  const historyRef = useRef({}); // { retrancaId: [snapshot, ...] }
  const saveTimer = useRef(null);
  const textareaRef = useRef(null);

  // Insert a markup token at the current cursor position of the TP textarea.
  // Keeps focus and selects the placeholder inside so the user can type over it.
  const insertMarkup = (open, placeholder, close) => {
    if (!draft) return;
    const ta = textareaRef.current;
    const current = draft.text || "";
    const start = ta?.selectionStart ?? current.length;
    const end = ta?.selectionEnd ?? current.length;
    const selected = current.slice(start, end);
    const inner = selected || placeholder;
    const token = `${open}${inner}${close}`;
    const next = current.slice(0, start) + token + current.slice(end);
    onDraftChange({ text: next });
    requestAnimationFrame(() => {
      const node = textareaRef.current;
      if (!node) return;
      node.focus();
      const selStart = start + open.length;
      const selEnd = selStart + inner.length;
      try { node.setSelectionRange(selStart, selEnd); } catch {}
    });
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const load = async () => {
    const r = await api.get(`/episodes/${id}`);
    setEp(r.data);
    if (!selectedId && r.data.retrancas.length > 0) {
      setSelectedId(r.data.retrancas[0].id);
    }
  };
  useEffect(() => {
    load();
    api.get("/gaveta").then((r) => setGaveta(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [id]);

  const selected = useMemo(
    () => ep?.retrancas.find((r) => r.id === selectedId) || null,
    [ep, selectedId]
  );

  useEffect(() => {
    if (selected) setDraft({ ...selected });
    else setDraft(null);
  }, [selectedId]); // eslint-disable-line

  const persistDraft = async (next, opts = {}) => {
    if (!next || !ep) return;
    // Push previous server-known state to history before saving (unless we're doing an undo/replace)
    if (!opts.skipHistory) {
      const prev = ep.retrancas.find((r) => r.id === next.id);
      if (prev) {
        const arr = historyRef.current[next.id] || [];
        arr.push(prev);
        if (arr.length > 20) arr.shift();
        historyRef.current[next.id] = arr;
        setHistoryVersion((v) => v + 1);
      }
    }
    setSaving(true);
    try {
      const r = await api.patch(`/episodes/${ep.id}/retrancas/${next.id}`, {
        title: next.title, type: next.type, responsible: next.responsible,
        presenter: next.presenter, approved: next.approved, text: next.text,
        tarja_chapeu: next.tarja_chapeu, tarja_linha1: next.tarja_linha1,
        tarja_linha2: next.tarja_linha2,
        overlays_url: next.overlays_url,
        uppercase: next.uppercase, vt_duration: next.vt_duration,
      });
      setEp(r.data);
    } finally {
      setSaving(false);
    }
  };

  const undoRetranca = async () => {
    if (!selectedId) return;
    const arr = historyRef.current[selectedId] || [];
    if (arr.length === 0) {
      toast.message("Nada para desfazer.");
      return;
    }
    const prev = arr.pop();
    setHistoryVersion((v) => v + 1);
    setDraft({ ...prev });
    if (saveTimer.current) clearTimeout(saveTimer.current);
    await persistDraft(prev, { skipHistory: true });
    toast.success("Alteração desfeita.");
  };

  const onDraftChange = (patch) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persistDraft(next), 500);
  };

  const addRetranca = async () => {
    const r = await api.post(`/episodes/${ep.id}/retrancas`, {
      title: "Nova retranca", type: "VT",
    });
    setEp(r.data);
    const last = r.data.retrancas[r.data.retrancas.length - 1];
    setSelectedId(last.id);
  };

  const pullFromGaveta = async (laudaId) => {
    try {
      const r = await api.post(`/episodes/${ep.id}/retrancas/from-gaveta/${laudaId}`);
      setEp(r.data);
      const last = r.data.retrancas[r.data.retrancas.length - 1];
      setSelectedId(last.id);
      toast.success("Lauda puxada da gaveta.");
    } catch (e) {
      toast.error("Não consegui puxar da gaveta.");
    }
  };

  const runDateAction = async (targetDate) => {
    if (!dateAction || !ep) return;
    const { mode, retrancaId } = dateAction;
    try {
      const path = mode === "copy" ? "copy-to" : "move-to";
      const r = await api.post(
        `/episodes/${ep.id}/retrancas/${retrancaId}/${path}`,
        { date: targetDate }
      );
      if (mode === "move") {
        // r.data is source episode after removal
        setEp(r.data);
        if (selectedId === retrancaId) setSelectedId(r.data.retrancas[0]?.id || null);
        toast.success(`Retranca movida para ${targetDate}.`);
      } else {
        toast.success(`Cópia criada em ${targetDate}.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Operação falhou.");
    } finally {
      setDateAction(null);
    }
  };

  const deleteRetranca = async (rid) => {
    const r = await api.delete(`/episodes/${ep.id}/retrancas/${rid}`);
    setEp(r.data);
    if (selectedId === rid) setSelectedId(r.data.retrancas[0]?.id || null);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = ep.retrancas.findIndex((r) => r.id === active.id);
    const newIdx = ep.retrancas.findIndex((r) => r.id === over.id);
    const reordered = arrayMove(ep.retrancas, oldIdx, newIdx);
    setEp({ ...ep, retrancas: reordered });
    const r = await api.post(`/episodes/${ep.id}/reorder`, {
      order: reordered.map((x) => x.id),
    });
    setEp(r.data);
  };

  const aiImprove = async () => {
    if (!draft?.text) {
      toast.message("Escreva algo primeiro para a IA melhorar.");
      return;
    }
    setAiBusy(true);
    try {
      const res = await fetch(`${API}/ai/assist`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: draft.text, instruction:
          "Reescreva este texto para o teleprompter. Frases curtas, claras, ritmo de fala. Mantenha em português brasileiro. Responda apenas com o texto reescrito." }),
      });
      if (!res.ok) throw new Error("AI request failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      const next = { ...draft, text: "" };
      setDraft(next);
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        acc += chunk;
        setDraft((d) => ({ ...d, text: acc }));
      }
      await persistDraft({ ...draft, text: acc });
      toast.success("Texto melhorado.");
    } catch (e) {
      toast.error("IA indisponível agora.");
    } finally {
      setAiBusy(false);
    }
  };

  const copyAll = async () => {
    if (!ep) return;
    const text = ep.retrancas
      .map((r) => `### ${r.title} (${r.type})\n${r.text || ""}`)
      .join("\n\n");
    await navigator.clipboard.writeText(text);
    toast.success("Roteiro copiado.");
  };

  if (!ep) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-500">Carregando...</div>;
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              data-testid="back-to-calendar"
              onClick={() => nav("/")}
              className="rounded-sm"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Calendário
            </Button>
            <img
              src={BRAND_MONOGRAM}
              alt="PodiUmScript"
              className="h-9 w-auto"
            />
            <div className="border-l border-zinc-200 pl-4">
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-zinc-500">Espelho</div>
              <div className="font-heading text-lg font-bold tracking-tight">
                {format(parseISO(ep.date), "EEEE, dd 'de' MMMM", { locale: ptBR })}
              </div>
              <input
                data-testid="episode-subtitle-input"
                value={ep.subtitle || ""}
                onChange={async (e) => {
                  const v = e.target.value;
                  setEp({ ...ep, subtitle: v });
                  if (window.__subTimer) clearTimeout(window.__subTimer);
                  window.__subTimer = setTimeout(async () => {
                    try {
                      const r = await api.patch(`/episodes/${ep.id}`, { subtitle: v });
                      setEp(r.data);
                    } catch {}
                  }, 500);
                }}
                placeholder="+ sub-nome desta edição"
                className="mt-0.5 w-56 text-xs text-[#152D4B] font-semibold italic bg-transparent border-0 outline-none placeholder:text-zinc-400 placeholder:font-normal placeholder:not-italic focus:placeholder:opacity-50"
              />
            </div>
          </div>
        </div>
        {/* Secondary toolbar: durations, timer and actions */}
        <div className="max-w-[1600px] mx-auto px-6 pb-3 pt-1 flex flex-wrap items-center justify-between gap-4 border-t border-zinc-100">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="hidden md:block">
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-zinc-500">Programa</div>
              <div className="font-mono text-lg font-bold tracking-tight text-[#152D4B]" data-testid="espelho-total-duration">
                {formatDuration(totalSeconds(ep.retrancas))}
              </div>
              <div className="text-[9px] uppercase tracking-wider text-emerald-600" data-testid="espelho-approved-duration">
                aprovado: {formatDuration(totalSeconds(ep.retrancas, true))}
              </div>
            </div>
            <div className="border-l border-zinc-200 pl-4">
              <EspelhoTimer
                episode={ep}
                totalSec={totalSeconds(ep.retrancas)}
                approvedSec={totalSeconds(ep.retrancas, true)}
                onPatchEpisode={async (patch) => {
                  try {
                    const r = await api.patch(`/episodes/${ep.id}`, patch);
                    setEp(r.data);
                  } catch (e) {
                    toast.error("Falha ao salvar horário.");
                  }
                }}
              />
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-zinc-400 mr-2">
              {saving ? "salvando..." : <span className="flex items-center gap-1"><Save className="w-3 h-3" />salvo</span>}
            </span>
            <Button variant="outline" size="sm" data-testid="copy-script-btn" onClick={copyAll} className="rounded-sm">
              <Copy className="w-4 h-4 mr-2" /> Copiar
            </Button>
            <Button variant="outline" size="sm" data-testid="export-pdf-btn" onClick={() => exportEspelhoPDF(ep)} className="rounded-sm">
              <FileDown className="w-4 h-4 mr-2" /> PDF
            </Button>
            <Button
              size="sm"
              data-testid="open-teleprompter-btn"
              onClick={() => setTpOpen(true)}
              className="rounded-sm bg-[#152D4B] hover:bg-[#0E1F36] text-white"
            >
              <Tv className="w-4 h-4 mr-2" /> Teleprompter
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
        {/* Left: retrancas list */}
        <aside>
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-[0.3em] font-bold text-zinc-500">Retrancas</div>
            <div className="flex items-center gap-1">
              <DropdownMenu onOpenChange={(open) => {
                if (open) api.get("/gaveta").then((r) => setGaveta(r.data)).catch(() => {});
              }}>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm" variant="outline"
                    data-testid="pull-from-gaveta-btn"
                    className="rounded-sm h-8 border-zinc-300"
                  >
                    <Archive className="w-3.5 h-3.5 mr-1" /> Gaveta <ChevronDown className="w-3 h-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-sm w-72 max-h-80 overflow-y-auto">
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em]">
                    Puxar da gaveta
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {gaveta.length === 0 ? (
                    <div className="px-2 py-4 text-xs text-zinc-500 text-center">
                      Gaveta vazia. Abra a Gaveta para criar materiais.
                    </div>
                  ) : (
                    gaveta.map((l) => (
                      <DropdownMenuItem
                        key={l.id}
                        data-testid={`pull-gaveta-${l.id}`}
                        onClick={() => pullFromGaveta(l.id)}
                        className="cursor-pointer"
                      >
                        <div className="flex items-center gap-2 w-full">
                          <span className="text-[9px] uppercase font-bold px-1 py-0.5 bg-zinc-100 text-zinc-700 rounded-sm">
                            {l.type}
                          </span>
                          <span className="text-sm truncate flex-1">{l.title}</span>
                        </div>
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                size="sm"
                data-testid="add-retranca-btn"
                onClick={addRetranca}
                className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white h-8"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Nova
              </Button>
            </div>
          </div>

          {ep.retrancas.length === 0 ? (
            <Card className="rounded-sm border-dashed border-zinc-300 shadow-none p-8 text-center">
              <div className="text-sm text-zinc-500">
                Crie a primeira retranca para começar o espelho.
              </div>
            </Card>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={ep.retrancas.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {ep.retrancas.map((r) => (
                    <SortableItem
                      key={r.id}
                      r={r}
                      active={r.id === selectedId}
                      onClick={() => setSelectedId(r.id)}
                      onDelete={() => deleteRetranca(r.id)}
                      onCopyTo={() => setDateAction({ mode: "copy", retrancaId: r.id })}
                      onMoveTo={() => setDateAction({ mode: "move", retrancaId: r.id })}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </aside>

        {/* Right: editor */}
        <section>
          {!draft ? (
            <Card className="rounded-sm border-dashed border-zinc-300 shadow-none p-16 text-center">
              <div className="font-heading text-2xl font-bold tracking-tight">Selecione uma retranca</div>
              <p className="text-zinc-500 mt-2">Ou crie uma nova para escrever o texto do teleprompter.</p>
            </Card>
          ) : (
            <Card className="rounded-sm border-zinc-200 shadow-none">
              <div className="px-6 pt-4 pb-2 flex items-center justify-end gap-2">
                <RetrancaHistoryDialog
                  episodeId={ep.id}
                  retrancaId={selectedId}
                  onRestored={(updatedEp) => {
                    setEp(updatedEp);
                    const r = updatedEp.retrancas.find((x) => x.id === selectedId);
                    if (r) setDraft({ ...r });
                  }}
                  trigger={
                    <Button
                      size="sm" variant="outline"
                      data-testid="open-history-btn"
                      className="rounded-sm h-8 text-zinc-600 border-zinc-300"
                      title="Ver histórico de alterações"
                    >
                      <History className="w-3.5 h-3.5 mr-1.5" />
                      Histórico
                    </Button>
                  }
                />
                <Button
                  size="sm" variant="outline"
                  data-testid="undo-retranca-btn"
                  onClick={undoRetranca}
                  disabled={!(historyRef.current[selectedId] || []).length}
                  className="rounded-sm h-8 text-zinc-600 border-zinc-300"
                  title="Desfazer última alteração salva"
                >
                  <Undo2 className="w-3.5 h-3.5 mr-1.5" />
                  Desfazer
                  {(historyRef.current[selectedId] || []).length > 0 && (
                    <span className="ml-1.5 text-[10px] text-zinc-500">
                      ({(historyRef.current[selectedId] || []).length})
                    </span>
                  )}
                </Button>
              </div>
              <div className="p-6 border-b border-zinc-100 pt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5 md:col-span-2">
                    <Label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">Título</Label>
                    <Input
                      data-testid="retranca-title-input"
                      value={draft.title}
                      onChange={(e) => onDraftChange({ title: e.target.value })}
                      className="rounded-sm border-zinc-300 focus-visible:ring-2 focus-visible:ring-[#152D4B] text-base font-semibold"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">Tipo</Label>
                    <Select value={draft.type} onValueChange={(v) => onDraftChange({ type: v })}>
                      <SelectTrigger data-testid="retranca-type-select" className="rounded-sm border-zinc-300">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-sm">
                        {TYPE_OPTIONS.map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">Apresentador</Label>
                    <Input
                      data-testid="retranca-presenter-input"
                      value={draft.presenter}
                      onChange={(e) => onDraftChange({ presenter: e.target.value })}
                      className="rounded-sm border-zinc-300 focus-visible:ring-2 focus-visible:ring-[#152D4B]"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">Responsável</Label>
                    <Input
                      data-testid="retranca-responsible-input"
                      value={draft.responsible}
                      onChange={(e) => onDraftChange({ responsible: e.target.value })}
                      className="rounded-sm border-zinc-300 focus-visible:ring-2 focus-visible:ring-[#152D4B]"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid="open-tarja-btn"
                      onClick={() => window.open(DEFAULT_OVERLAYS_SLOT, "_blank", "noopener,noreferrer")}
                      className="rounded-sm mt-2 h-8"
                      title="Abrir o controle da tarja no overlays.uno"
                    >
                      <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Tarja
                    </Button>
                  </div>
                  <div className="flex items-end">
                    <label
                      data-testid="retranca-approved-toggle"
                      className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer"
                    >
                      <Checkbox
                        checked={draft.approved}
                        onCheckedChange={(v) => onDraftChange({ approved: !!v })}
                        data-testid="retranca-approved-checkbox"
                        className="rounded-sm"
                      />
                      Aprovado
                    </label>
                  </div>
                </div>
              </div>

              <div className="p-6 relative">
                <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                  <Label className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">Texto do teleprompter</Label>
                  <div className="flex items-center gap-2">
                    {draft.type === "VT" && (
                      <div className="flex items-center gap-1 border border-zinc-300 rounded-sm px-2 h-8">
                        <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mr-1">VT</span>
                        <Input
                          data-testid="retranca-vt-min"
                          type="number"
                          min={0}
                          value={Math.floor((draft.vt_duration || 0) / 60)}
                          onChange={(e) => {
                            const m = Math.max(0, parseInt(e.target.value || "0", 10));
                            const s = (draft.vt_duration || 0) % 60;
                            onDraftChange({ vt_duration: m * 60 + s });
                          }}
                          className="w-10 h-6 border-0 focus-visible:ring-0 p-0 text-sm text-right"
                        />
                        <span className="text-[10px] text-zinc-500">min</span>
                        <span className="text-zinc-300">:</span>
                        <Input
                          data-testid="retranca-vt-sec"
                          type="number"
                          min={0}
                          max={59}
                          value={(draft.vt_duration || 0) % 60}
                          onChange={(e) => {
                            const s = Math.min(59, Math.max(0, parseInt(e.target.value || "0", 10)));
                            const m = Math.floor((draft.vt_duration || 0) / 60);
                            onDraftChange({ vt_duration: m * 60 + s });
                          }}
                          className="w-10 h-6 border-0 focus-visible:ring-0 p-0 text-sm text-right"
                        />
                        <span className="text-[10px] text-zinc-500">seg</span>
                      </div>
                    )}
                    <button
                      type="button"
                      data-testid="retranca-uppercase-toggle"
                      onClick={() => onDraftChange({ uppercase: !draft.uppercase })}
                      className={`text-[10px] uppercase tracking-wider font-bold h-8 px-2 border rounded-sm ${draft.uppercase ? "bg-[#152D4B] text-white border-[#152D4B]" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-500"}`}
                      title="Alternar CAIXA ALTA no teleprompter"
                    >
                      AA
                    </button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="ai-assist-btn"
                      onClick={aiImprove}
                      disabled={aiBusy}
                      className="rounded-sm border-[#152D4B] text-[#152D4B] hover:bg-[#152D4B] hover:text-white"
                    >
                      <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                      {aiBusy ? "Melhorando..." : "IA — melhorar texto"}
                    </Button>
                  </div>
                </div>
                <Textarea
                  data-testid="retranca-text-input"
                  ref={textareaRef}
                  value={draft.text}
                  onChange={(e) => onDraftChange({ text: e.target.value })}
                  rows={18}
                  placeholder="Escreva aqui o texto que será lido pelos apresentadores..."
                  className={`rounded-sm border-zinc-300 focus-visible:ring-2 focus-visible:ring-[#152D4B] font-mono text-[15px] leading-relaxed resize-none ${draft.uppercase ? "uppercase" : ""}`}
                />
                {/* Markup quick-insert buttons */}
                <div
                  data-testid="prompter-markup-buttons"
                  className="mt-2 flex flex-wrap items-center gap-2"
                >
                  <button
                    type="button"
                    data-testid="markup-insert-presenter"
                    onClick={() => insertMarkup("[", "apresentador", "]")}
                    className="h-8 px-3 rounded-sm border border-zinc-300 bg-white text-[#152D4B] text-xs font-mono tracking-wide hover:bg-zinc-50 hover:border-[#152D4B]"
                    title="Inserir indicação de apresentador (branco)"
                  >
                    [apresentador]
                  </button>
                  <button
                    type="button"
                    data-testid="markup-insert-vt"
                    onClick={() => insertMarkup("{", "RODA VT", "}")}
                    className="h-8 px-3 rounded-sm border border-orange-500 bg-orange-50 text-orange-700 text-xs font-mono tracking-wide hover:bg-orange-500 hover:text-white"
                    title="Inserir comando de rodar VT (laranja)"
                  >
                    {"{RODA VT}"}
                  </button>
                  <button
                    type="button"
                    data-testid="markup-insert-credito"
                    onClick={() => insertMarkup("(", "crédito", ")")}
                    className="h-8 px-3 rounded-sm border border-sky-500 bg-sky-50 text-sky-700 text-xs font-mono tracking-wide hover:bg-sky-500 hover:text-white"
                    title="Inserir crédito/indicação técnica (azul)"
                  >
                    (crédito)
                  </button>
                  <button
                    type="button"
                    data-testid="markup-insert-cam"
                    onClick={() => insertMarkup("((", "CAM1", "))")}
                    className="h-8 px-3 rounded-sm border border-red-500 bg-red-50 text-red-700 text-xs font-mono tracking-wide hover:bg-red-600 hover:text-white"
                    title="Inserir câmera/aviso (vermelho piscando)"
                  >
                    ((CAM1))
                  </button>
                  <div className="flex-1" />
                  <button
                    type="button"
                    data-testid="toggle-sublauda"
                    onClick={() => onDraftChange({ notes: draft.notes ? "" : " " })}
                    className={`h-8 px-3 rounded-sm text-xs uppercase tracking-wider font-bold border ${draft.notes ? "bg-amber-400 text-black border-amber-400 hover:bg-amber-500" : "bg-white text-zinc-600 border-zinc-300 hover:border-amber-500 hover:text-amber-700"}`}
                    title="Mostrar/esconder bloco de notas (sublauda) — não aparece no teleprompter"
                  >
                    {draft.notes ? "− Sublauda" : "+ Sublauda"}
                  </button>
                </div>
                {draft.notes && (
                  <div data-testid="sublauda-block" className="mt-3 rounded-sm border border-amber-300 bg-amber-50/60">
                    <div className="flex items-center justify-between px-3 py-1.5 border-b border-amber-200">
                      <Label className="text-[10px] uppercase tracking-[0.25em] font-bold text-amber-700">
                        Sublauda · nota interna
                      </Label>
                      <span className="text-[10px] uppercase tracking-wider text-amber-700/70">não vai para o teleprompter</span>
                    </div>
                    <Textarea
                      data-testid="retranca-notes-input"
                      value={draft.notes}
                      onChange={(e) => onDraftChange({ notes: e.target.value })}
                      rows={4}
                      placeholder="Observações, roteiro reserva, pontos a checar, link de referência..."
                      className="rounded-none border-0 bg-transparent focus-visible:ring-0 font-mono text-[13px] leading-relaxed resize-none"
                    />
                  </div>
                )}
                <div
                  data-testid="prompter-markup-legend"
                  className="mt-2 flex items-center gap-2 flex-wrap text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2"
                >
                  <span className="text-zinc-400 normal-case tracking-normal font-sans mr-1">
                    Cores no teleprompter:
                  </span>
                  <code className="text-white bg-black px-1.5 py-0.5">[texto]</code>
                  <span className="text-white normal-case tracking-normal font-sans mr-1">Branco</span>
                  <code className="text-orange-400 bg-black px-1.5 py-0.5">{"{texto}"}</code>
                  <span className="text-orange-400 normal-case tracking-normal font-sans mr-1">Laranja</span>
                  <code className="text-sky-400 bg-black px-1.5 py-0.5">(texto)</code>
                  <span className="text-sky-400 normal-case tracking-normal font-sans mr-1">Azul</span>
                  <code className="text-red-500 bg-black px-1.5 py-0.5 animate-pulse">((texto))</code>
                  <span className="text-red-500 normal-case tracking-normal font-sans">Vermelho piscando</span>
                </div>
                <div className="text-xs text-zinc-400 mt-2 flex items-center justify-between">
                  <span>{draft.text.length} caracteres</span>
                  <span data-testid="retranca-reading-time">
                    {draft.type === "VT" && (draft.vt_duration || 0) > 0
                      ? `VT ${formatDuration(draft.vt_duration)}`
                      : `Leitura ~${formatDuration(readingSeconds(draft.text))}`}
                  </span>
                </div>
              </div>
            </Card>
          )}
        </section>
      </main>

      {tpOpen && (
        <Teleprompter
          episode={ep}
          onClose={() => setTpOpen(false)}
          onExportPdf={() => exportTeleprompterPDF(ep)}
        />
      )}

      <DatePickerDialog
        open={!!dateAction}
        onOpenChange={(o) => { if (!o) setDateAction(null); }}
        title={dateAction?.mode === "move" ? "Mover retranca para..." : "Reexibir em..."}
        description={
          dateAction?.mode === "move"
            ? "A retranca sairá desta edição e vai para a data escolhida."
            : "Uma cópia da retranca será criada na data escolhida (a original fica onde está)."
        }
        confirmLabel={dateAction?.mode === "move" ? "Mover" : "Reexibir"}
        onConfirm={runDateAction}
      />
    </div>
  );
}
